import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import {
  getGetTemplateListQueryKey,
  useCreateTemplate,
  useGetTemplateList,
} from "~/api/custom-reports/custom-reports";
import { Button } from "~/components/ui/button";
import { useToast } from "~/hooks/use-toast";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { CreateTemplateModal } from "./CreateTemplateModal";
import { ImportTemplateButton } from "./ImportTemplateButton";
import { TemplatePanel } from "./TemplatePanel";
import { parseImportedTemplate } from "./importExportUtils";

export const SettingsReports: FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { organizationId = "" } = useParams();
  const { data: templatesData } = useGetTemplateList(organizationId, {
    query: {
      refetchInterval: (query) => {
        if (!query.state.data) {
          return false;
        }

        const templates = query.state.data.templateList ?? [];
        const needsPolling = templates.some(
          (template) =>
            template.status === "pending" || template.status === "processing"
        );

        return needsPolling ? 3000 : false; // Poll every 3 seconds
      },
      refetchIntervalInBackground: true, // Continue polling even when tab is not active
    },
  });
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const createTemplate = useCreateTemplate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetTemplateListQueryKey(organizationId),
        });
      },
    },
  });
  useTrackPageVisit("app/settings_reports/visit");

  const templateList = templatesData?.templateList ?? [];

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file) return;

    try {
      const text = await file.text();
      const result = parseImportedTemplate(text, file.size);

      if (!result.success) {
        let messageId = "importTemplate.invalid";
        let defaultMessage = "Invalid template JSON";

        if (result.error === "FILE_TOO_LARGE") {
          messageId = "importTemplate.fileTooLarge";
          defaultMessage = "File size exceeds the maximum limit of 25MB";
        }

        toast({
          title: intl.formatMessage({
            id: messageId,
            defaultMessage,
          }),
          variant: "destructive",
        });
        return;
      }

      const parsed = result.data;

      const isDuplicate = templateList.some((existingTemplate) => {
        if (
          existingTemplate.name !== parsed.name ||
          existingTemplate.definition !== parsed.definition
        ) {
          return false;
        }

        return true;
      });

      if (isDuplicate) {
        toast({
          title: intl.formatMessage({
            id: "importTemplate.duplicate",
            defaultMessage: "Template already exists",
          }),
          description: intl.formatMessage({
            id: "importTemplate.duplicate.description",
            defaultMessage:
              "A template with the same name and definition already exists. No new template was created.",
          }),
        });
        return;
      }

      createTemplate.mutate(
        {
          organizationId,
          data: {
            name: parsed.name,
            definition: parsed.definition,
            sections: parsed.sections.map(
              ({ title, purpose, prompt, sectionType, config }) => ({
                title,
                purpose:
                  sectionType === "docList"
                    ? "List all document titles by category"
                    : purpose,
                prompt: sectionType === "docList" ? "docList" : prompt,
                contextType: sectionType === "docList" ? "docList" : "snapshot",
                config: config
                  ? {
                      excludedCategoryNames: config.excludedCategoryNames,
                    }
                  : undefined,
              })
            ),
          },
        },
        {
          onSuccess: () => {
            toast({
              title: intl.formatMessage({
                id: "importTemplate.success",
                defaultMessage: "Template imported",
              }),
            });
          },
          onError: () => {
            toast({
              title: intl.formatMessage({
                id: "importTemplate.error",
                defaultMessage: "Failed to import template",
              }),
            });
          },
        }
      );
    } catch {
      toast({
        title: intl.formatMessage({
          id: "importTemplate.invalid",
          defaultMessage: "Invalid template JSON",
        }),
        variant: "destructive",
      });
    }
  };

  const allTemplates = [...templateList].sort((a, b) => {
    // Helper function to check if template is new (less than 24 hours old)
    const isNew = (template: any) => {
      if (!template.createdAt) return false;
      const now = new Date().getTime();
      const created = new Date(template.createdAt).getTime();
      const hoursDiff = (now - created) / (1000 * 60 * 60);
      return hoursDiff < 24;
    };

    const isCompletedNew = (template: any) => {
      return (
        isNew(template) && (!template.status || template.status === "completed")
      );
    };

    const aIsCompletedNew = isCompletedNew(a);
    const bIsCompletedNew = isCompletedNew(b);

    // Completed New templates first, sorted by createdAt descending
    if (aIsCompletedNew && !bIsCompletedNew) return -1;
    if (!aIsCompletedNew && bIsCompletedNew) return 1;
    if (aIsCompletedNew && bIsCompletedNew) {
      const aCreated = new Date(a.createdAt).getTime();
      const bCreated = new Date(b.createdAt).getTime();
      return bCreated - aCreated; // Descending order (newest first)
    }

    // Draft templates second
    if (a.isDraft && !b.isDraft) return -1;
    if (!a.isDraft && b.isDraft) return 1;

    // Within drafts, sort by status: failed -> pending/processing -> completed
    if (a.isDraft && b.isDraft) {
      const statusPriority = {
        failed: 0,
        pending: 1,
        processing: 1,
        completed: 2,
      };

      const aPriority = statusPriority[a.status] ?? 3;
      const bPriority = statusPriority[b.status] ?? 3;

      return aPriority - bPriority;
    }

    // Default template first among regular templates (not new, not draft)
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;

    return 0;
  });

  return (
    <div className="flex flex-col gap-6 mt-2 w-full">
      <div className="flex justify-between items-center gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage id="settings.reports" defaultMessage="Reports" />
          </h3>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.reports.description"
              defaultMessage="Create and manage reusable report templates used by Sky AI to create reports from your case documents"
            />
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <ImportTemplateButton
            organizationId={organizationId}
            onImportJson={handleImportFile}
          />
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            <FormattedMessage
              id="settings.createTemplate"
              defaultMessage="Template"
            />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {allTemplates.map((template) => (
          <TemplatePanel
            key={template.id}
            template={template}
            isDefault={template.isDefault}
            hideMenu={template.isDefault}
          />
        ))}
      </div>
      <CreateTemplateModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        organizationId={organizationId}
      />
    </div>
  );
};
